import { FC, useState } from 'react';
import Icon, { Icons } from '../Icon';
import { HeaderProps } from './';
import HeaderLink from './HeaderLink';
import { Grid } from '../Grid';
import HeaderNavigation from './HeaderNavigation';
import { connect, useDispatch, useSelector } from 'react-redux';
import FilterDuck from 'src/redux/filters/filter.duck';
import {
    BRAND,
    isBrandOV,
    isBrandDS,
    isBrandAC,
    isMarketGB,
    IS_B2B,
    isBrandAP,
    isMarketFR,
    ROLES,
    B2B_URL,
    IS_B2C,
    B2C_URL,
} from 'src/constants/main';
import routes from 'src/constants/routes';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { buildParameterizedPathname } from '@utils/url.utils';
import { Redux } from 'src/redux/redux.interface';
import { CarJourneyType } from 'src/services';
import GlobalDuck from 'src/redux/global/global.duck';
import Link from '../CustomNextLink';
import useTranslations from '@hooks/useTranslations';
import { useSkipToContent } from '@hooks/useSkipToContent';
import React from 'react';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { useGTM } from '@hooks/useGTM';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { useRouter } from 'next/router';
import {useGlobalDuck} from "@hooks/useGlobalDuck";

const HeaderTemplate: FC<HeaderProps> = ({ className, title, subtitle, links }) => {
    const dispatch = useDispatch();
    const { pushToDataLayer } = useGTM();
    const [showMenu, setshowMenu] = useState(false);
    const { carJourney } = useGlobalDuck();
    const { t } = useTranslations();
    const router = useRouter();
    const { handleSkipToContent } = useSkipToContent();
    const isEnvSwitcherEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_ENV_SWITCHER_ENABLED);
    let path = buildParameterizedPathname(routes.ROOT, carJourney);

    // deeplink functionality - reset filter after click on logo
    path = `${path}${path.includes('?') ? '&' : '?'}resetFilters=true`;

    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
    const handleLogoClick = () => {
        pushToDataLayer({
            event: 'uaevent',
            eventCategory: 'Logo',
            eventAction: 'Redirection::Internal',
            eventLabel: 'Home Page',
        });
        // Reset loading state so that in case of unfinished requests homepage doesn't get stuck with loading
        dispatch(UIDuck.resetLoading());
    };

    const handleSwitchClick = () => {
        pushToDataLayer({
            event: 'uaevent',
            eventCategory: 'Header',
            eventAction: 'Redirection::External',
            eventLabel: IS_B2C ? 'Professionnel' : 'Particulier',
        });

        return (window.location.href = IS_B2C ? B2B_URL : B2C_URL);
    };

    if (typeof subtitle === 'string') {
        subtitle = subtitle.trim();
    }

    const BrandsProLabel = () => {
        switch (true) {
            case isBrandDS: {
                return (
                    <p className="title-primary">
                        <span>{BRAND}</span> {title}
                        {Boolean(IS_B2B) && <span className="pro-label-b2b">PRO</span>}
                    </p>
                );
            }

            case isBrandOV: {
                return (
                    <>
                        <a onClick={handleLogoClick} className="title-primary">
                            {title}
                            {Boolean(IS_B2B) && <span className="pro-label-b2b">Pro</span>}
                        </a>
                        {subtitle && <h1 className="title-secondary">{subtitle}</h1>}
                    </>
                );
            }

            case isBrandAC: {
                return (
                    <p className="title-primary">
                        <div className="title-container">
                            <p>{title}</p>
                        </div>
                        {B2B_URL && B2C_URL && isEnvSwitcherEnabled && (
                            <div className="toggle-switch-container">
                                <div className="b2b-switch switch-vertical">
                                    <input
                                        onChange={handleSwitchClick}
                                        id="toggle-a"
                                        type="radio"
                                        name="switch"
                                        checked={IS_B2C}
                                    />
                                    <AccessibleButton tabIndex={0}>
                                        <label htmlFor="toggle-a">{t('toggle.B2C')}</label>
                                    </AccessibleButton>
                                    <input
                                        onChange={handleSwitchClick}
                                        id="toggle-b"
                                        type="radio"
                                        name="switch"
                                        checked={IS_B2B}
                                    />
                                    <AccessibleButton tabIndex={0}>
                                        <label htmlFor="toggle-b">{t('toggle.B2B')}</label>
                                    </AccessibleButton>
                                    <span className="toggle-outside">
                                        <button onClick={handleSwitchClick} className="toggle-inside" />
                                    </span>
                                </div>
                            </div>
                        )}
                    </p>
                );
            }

            default: {
                return (
                    <>
                        <p className="title-primary">
                            {title}
                            {Boolean(IS_B2B && isBrandAP) && <span className="pro-label-b2b">PRO</span>}
                        </p>
                        {subtitle && !(isMarketGB && isBrandOV) && <h1 className="title-secondary">{subtitle}</h1>}
                    </>
                );
            }
        }
    };

    return (
        <div
            style={showMenu ? { overflow: 'unset' } : {}}
            className={`${className} ${router?.pathname?.includes(routes.SELECTOR) ? 'withMobileBorder' : ''}`}
        >
            <Grid className="header-container">
                <div className="skip-content">
                    <a href="#main" onClick={handleSkipToContent}>
                        {t('accessibility.skipToStep')}
                    </a>
                </div>
                <div className="main-section">
                    <Link href={path}>
                        <a
                            id="TESTING_LOGO"
                            data-testid={!isBrandAC ? 'TESTING_LOGO_BTN' : ''}
                            className="logo"
                            onClick={handleLogoClick}
                        >
                            <Icon
                                altId="svgImgLogo"
                                altText={t('myaccount.nav.home')}
                                role={ROLES.IMG}
                                className="header-logo-icon"
                                width={86}
                                height={60}
                                name={Icons.Logo}
                            />
                        </a>
                    </Link>
                    <div className="title-section">
                        <BrandsProLabel />
                    </div>
                    {!isBrandOV && isMarketGB && (
                        <div className="link-section">
                            {links.map(({ label }, index) => (
                                <HeaderLink
                                    key={index}
                                    id={`TESTING_HEADER_${index}`}
                                    destination={{
                                        type: label.type === 'link' ? 'link' : 'href',
                                        to: label.type === 'file' ? label.file : label.href,
                                    }}
                                    title={label.title}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className="center-logo-section">
                    <Link href={path}>
                        <a
                            data-testid={isBrandAC ? 'TESTING_LOGO_BTN' : ''}
                            className="logo"
                            onClick={() => handleLogoClick()}
                        >
                            <Icon width={79} color={'red'} height={80} name={Icons.Logo} />
                        </a>
                    </Link>
                </div>
                <HeaderNavigation showMenu={showMenu} setshowMenu={setshowMenu} />
            </Grid>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    paymentJourney: FilterDuck.getPaymentJourneyType(state),
});

export default connect(mapStateToProps)(HeaderTemplate);
